import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import jwtDecode from 'jwt-decode'
import {
  Container,
  SectionContainer,
  SectionOne,
  HorizontalDivider,
  SectionTwo,
  SectionOneItem,
  SectionOneItemNumber,
  SectionOneItemDescription,
  CompanyRow,
  ContainerActions,
  TextViewMore,
  EditContainer,
  TextEdit,
} from './styled'
import { Button, StrokedButton } from 'views/components/UI/Buttons'
import ViewMoreDiv from 'views/components/UI/ViewMoreDiv'
import EditIcon from 'assets/icons/EditCoin'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { superAdminRoutes } from 'router/routes'
import getAllCompaniesById, { ICompanies } from 'services/companies/getAllCompaniesById'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import getDataDashboard from 'services/common/super-admin-index'
import { useTranslation } from 'react-i18next'
import { setCompanyId } from 'views/screens/Companies/store'
import skillIcon from 'assets/images/icon-skills-card.png'
import betesaIcon from 'assets/images/icon-betesa-card.png'
import test360Icon from 'assets/images/icon-test360-card.png'
import TestCard from './test-card'

interface IDataDashboard {
  numberOfTests: number
  numberOfCompanies: number
  numberOfCompanyUsers: number
  numberOfResponsibleCompanies: number
  numberOfBetesas: number
  numberOfUninterpretedBetesas: number
  numberOfCompetencies: number
  numberOfTest360: number
}

const headers = [
  {
    key: 'name',
    name: 'Nombre',
  },
]

const ActionComponent = (item: any) => {
  const { id } = item
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleNavigate = (destination: string) => (event: React.PointerEvent<HTMLAnchorElement>) => {
    if (event.button !== 0) return

    event.preventDefault()
    navigate(destination)
  }

  return (
    <ContainerActions>
      <Link to={`/company?companyId=${id}`} onPointerDown={handleNavigate(`/company?companyId=${id}`)}>
        <TextViewMore>Ver más</TextViewMore>
      </Link>
      <Link
        to={`/company?companyId=${id}&edit=true`}
        onPointerDown={handleNavigate(`/company?companyId=${id}&edit=true`)}
      >
        <EditContainer
          onClick={() => {
            dispatch(setCompanyId(id))
          }}
        >
          <EditIcon />
          <TextEdit>Editar</TextEdit>
        </EditContainer>
      </Link>
    </ContainerActions>
  )
}

const Index = () => {
  const { t } = useTranslation()
  const { token } = useSelector((state: any) => state.user)
  const decoded: any = jwtDecode(token)
  const navigate = useNavigate()
  const [dataDashboard, setDataDashboard] = useState<IDataDashboard | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)

  const [companiesData, setCompaniesData] = useState({
    companies: [] as ICompanies[],
    total_pages: 0,
  })

  const fetchGetUserCompanies = useCallback((page = 1, limit = 10) => {
    getAllCompaniesById(page, limit).then(({ companies, total_pages }) => {
      setCompaniesData({ companies, total_pages })
      setLoading(false)
    })
  }, [])

  const getUserCompanies = useCallback(
    (searchParam: string, page = 1, limit = 5) => {
      setSearchValue(searchParam)
      if (searchParam === '') {
        fetchGetUserCompanies(page, limit)
      } else {
        getAllCompaniesById(page, limit, searchParam).then(({ companies, total_pages }) => {
          setLoading(false)
          setCompaniesData({ companies, total_pages })
        })
      }
    },
    [fetchGetUserCompanies]
  )

  useEffect(() => {
    getDataDashboard(decoded.id).then((res: any) => {
      setDataDashboard(res.data)
    })
  }, [decoded.id, searchValue])

  useEffect(() => {
    if (searchValue.length > 0) {
      getUserCompanies(searchValue)
    }
  }, [searchValue, getUserCompanies])

  useEffect(() => {
    if (searchValue.length <= 0) {
      fetchGetUserCompanies()
    }
  }, [searchValue, fetchGetUserCompanies])

  const formatNumber = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }

  const handleClickInterpretation = () => {
    navigate('/interpretation')
  }

  return (
    <MainLayout>
      <Container>
        {!loading ? (
          <>
            {dataDashboard && (
              <SectionContainer>
                <SectionOne>
                  <SectionOneItem>
                    <SectionOneItemNumber>
                      {formatNumber(dataDashboard.numberOfUninterpretedBetesas)}
                    </SectionOneItemNumber>
                    <SectionOneItemDescription>Betesas por interpretar</SectionOneItemDescription>
                    <StrokedButton
                      onClick={handleClickInterpretation}
                      style={{ width: 'fit-content', fontSize: '14px', padding: '7px 12px' }}
                    >
                      Interpretar Betesa
                    </StrokedButton>
                  </SectionOneItem>
                  <HorizontalDivider />
                  <SectionOneItem>
                    <SectionOneItemNumber>{formatNumber(dataDashboard.numberOfTests)}</SectionOneItemNumber>
                    <SectionOneItemDescription>Total de pruebas enviadas</SectionOneItemDescription>
                  </SectionOneItem>
                  <HorizontalDivider />
                  <SectionOneItem>
                    <SectionOneItemNumber>{dataDashboard.numberOfCompanies}</SectionOneItemNumber>
                    <SectionOneItemDescription>Total compañías vinculadas</SectionOneItemDescription>
                  </SectionOneItem>
                  <HorizontalDivider />
                  <SectionOneItem>
                    <SectionOneItemNumber>{formatNumber(dataDashboard.numberOfCompanyUsers)}</SectionOneItemNumber>
                    <SectionOneItemDescription>Total usuarios</SectionOneItemDescription>
                  </SectionOneItem>
                  <HorizontalDivider />
                  <SectionOneItem>
                    <SectionOneItemNumber>
                      {formatNumber(dataDashboard.numberOfResponsibleCompanies)}
                    </SectionOneItemNumber>
                    <SectionOneItemDescription>Compañías a mi cargo</SectionOneItemDescription>
                  </SectionOneItem>
                </SectionOne>
                <SectionTwo>
                  <TestCard image={betesaIcon} title="Betesa" testData={formatNumber(dataDashboard.numberOfBetesas)} />
                  <TestCard
                    image={skillIcon}
                    title={t('global.common.skillText')}
                    testData={formatNumber(dataDashboard.numberOfCompetencies)}
                  />
                  <TestCard image={test360Icon} title="360°" testData={formatNumber(dataDashboard.numberOfTest360)} />
                </SectionTwo>
              </SectionContainer>
            )}
            <CompanyRow>
              <h2 style={{ fontSize: 32 }}>Compañías a mi cargo</h2>
              <Link to={superAdminRoutes.CreateCompany}>
                <Button type="button">Nueva compañía</Button>
              </Link>
            </CompanyRow>
            <BluesiteTableComponent
              ActionComponent={ActionComponent}
              data={companiesData.companies}
              headers={headers}
              searchBar={true}
              searchLabel="Buscar empresa por:"
              totalPages={1}
              handlePagination={fetchGetUserCompanies}
              onSearch={getUserCompanies}
              handleSearchFunction={() => getUserCompanies(searchValue)}
              currentPage={1}
              limit={5}
              loading={loading}
            />

            <ViewMoreDiv>
              <Link
                to={superAdminRoutes.Companies}
                style={{ color: '#2D749C', fontSize: 14, marginBottom: 30, fontWeight: '700' }}
              >
                {' '}
                Ver más
              </Link>
            </ViewMoreDiv>
          </>
        ) : (
          <ModalLoader isLoading={loading} />
        )}
      </Container>
    </MainLayout>
  )
}

export default Index
